import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSessionPersistence } from '../hooks/useSessionPersistence';
import { apiService } from '../utils/api';
import ForgotPasswordModal from './ui/ForgotPasswordModal';
import { ResponsiveContainer, ResponsiveContent } from './ui/ResponsiveLayout';
import BrandSymbol from './ui/BrandSymbol';
import { API_BASE_URL } from '../config/api';
import { resolvePostLoginRoute } from '../utils/postLoginRouting';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  
  const { updateUserInfo, sessionExpired } = useSessionPersistence();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [searchParams] = useSearchParams();
  const inviteTokenFromUrl = searchParams.get('invite');
  const inviteRoleFromUrl = (searchParams.get('role') ?? 'executor') as 'executor' | 'beneficiary';

  // Optional: prefill email if sent from Frame129
  useEffect(() => {
    const emailFromUrl = searchParams.get('email');
    if (emailFromUrl && !email) {
      setEmail(emailFromUrl.toLowerCase().trim());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, email]);

  const [error, setError] = useState<string | null>(null);
  const [requiresVerification, setRequiresVerification] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  
  // Lockout state
  const [isLocked, setIsLocked] = useState(false);
  const [lockCountdown, setLockCountdown] = useState(0); // seconds remaining

  // Resend verification state
  const [isResendingVerification, setIsResendingVerification] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Countdown timer for locked accounts
  useEffect(() => {
    if (lockCountdown > 0) {
      const timer = setTimeout(() => {
        setLockCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (isLocked && lockCountdown === 0) {
      // Lockout expired
      setIsLocked(false);
      setError(null);
    }
  }, [lockCountdown, isLocked]);

  // Resend verification cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleResendVerification = async () => {
    if (resendCooldown > 0 || isResendingVerification || !email) return;

    setIsResendingVerification(true);
    setResendSuccess(false);

    try {
      const response = await fetch(`${API_BASE_URL}/api/accounts/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() })
      });

      const data = await response.json();

      if (data.success) {
        setResendSuccess(true);
        setResendCooldown(60);
      } else {
        setError(data.message || 'Failed to resend verification email');
      }
    } catch (err) {
      console.error('Resend verification error:', err);
      setError('Failed to resend verification email');
    } finally {
      setIsResendingVerification(false);
    }
  };

  // Check for error message from MFA redirect
  useEffect(() => {
    const loginError = sessionStorage.getItem('login_error');
    if (loginError) {
      setError(loginError);
      sessionStorage.removeItem('login_error');
    }
  }, []);

  // Format countdown time
  const formatCountdown = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const acceptExecutorInviteIfPresent = async (accountToken: string) => {
    if (!inviteTokenFromUrl) return;

    const endpoint = inviteRoleFromUrl === 'beneficiary'
      ? '/api/beneficiaries/accept-invite'
      : '/api/executors/accept-invite';

    try {
      const resp = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accountToken}`
        },
        body: JSON.stringify({ inviteToken: inviteTokenFromUrl })
      });

      if (resp.ok) {
        console.log(`[LoginPage] Successfully accepted ${inviteRoleFromUrl} invite`);

        // âœ… Strip invite/email params from URL so refresh doesn't re-trigger accept-invite
        navigate('/login', { replace: true });
      } else {
        const errJson = await resp.json().catch(() => ({}));
        console.error('[LoginPage] Failed to accept invite:', errJson);
      }
    } catch (err) {
      console.error('[LoginPage] Error accepting invite:', err);
    }
  };

  const navigatePostLogin = async (accountToken: string) => {
    const route = await resolvePostLoginRoute(accountToken);

    if (route.state) {
      navigate(route.path, { state: route.state });
    } else {
      navigate(route.path);
    }
  };


  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setRequiresVerification(false);
    setIsSubmitting(true);
    
    try {
      const result = await apiService.login(email.trim(), password);
      
      // Check if account is locked
      if (result.lockedUntil || result.remainingSeconds) {
        setIsLocked(true);
        setLockCountdown(result.remainingSeconds || 0);
        setError(result.message || 'Account temporarily locked');
        return;
      }
      
      // Check if MFA is required
      if (result.success && result.requiresMFA) {
        if (!result.userId || !result.email) {
          setError('MFA required but missing session details. Please try again.');
          setIsSubmitting(false);
          return;
        }

        // Store MFA session data for the verification page
        sessionStorage.setItem('mfa_userId', result.userId);
        sessionStorage.setItem('mfa_email', result.email);
        sessionStorage.setItem('mfa_name', result.name ?? '');

        // Navigate to MFA verification page
        // before that store invite token if present (FIX)
        if (inviteTokenFromUrl) {
          sessionStorage.setItem('pending_invite_token', inviteTokenFromUrl);
          sessionStorage.setItem('pending_invite_role', inviteRoleFromUrl);
        }
        navigate('/mfa-verify');
        return;
      }

      if (!result.success) {
        setError(result.message || 'Login failed');
        // Check if this is a verification error
        if (result.requiresVerification) {
          setRequiresVerification(true);
        }
      } else if (result.user && result.token) {
        // Update session info and wait for it to complete
        await updateUserInfo({
          email: result.user.email,
          name: result.user.name,
          phone: result.user.phone,
          accountId: result.user.id,
          accountToken: result.token
        });

        await acceptExecutorInviteIfPresent(result.token); // addition here

        await navigatePostLogin(result.token);
      }
    } catch (e: unknown) {
      // Check if the error response contains lockout info
      if (e.lockedUntil || e.remainingSeconds) {
        setIsLocked(true);
        setLockCountdown(e.remainingSeconds || 0);
        setError(e.message || 'Account temporarily locked');
      } else {
        setError(e.message || 'Login failed');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPasswordClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowForgotPassword(true);
  };

  const handleCreateAccount = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/'); // Navigate to the beginning of the app flow
  };


  return (
    <ResponsiveContainer>
      <ResponsiveContent className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8">
            <BrandSymbol size="4xl" variant="dark" showText />
          </div>
          
          <div className="bg-white border border-gray-300 rounded-lg shadow-lg p-8">
            <div className="flex justify-center mb-6">
              <h1 className="text-2xl font-semibold text-gray-900">Log In</h1>
            </div>

            {sessionExpired && (
              <div className="mb-6 bg-orange-50 border border-orange-200 rounded-md p-4">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-orange-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <div>
                    <h3 className="text-sm font-semibold text-orange-800 mb-1">Session Expired</h3>
                    <p className="text-sm text-orange-700">
                      Your session expired due to inactivity. Please log in again to continue.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Account Locked Warning */}
            {isLocked && lockCountdown > 0 && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <div>
                    <h3 className="text-sm font-semibold text-red-800 mb-1">Account Temporarily Locked</h3>
                    <p className="text-sm text-red-700 mb-2">
                      Too many failed login attempts. Please try again in:
                    </p>
                    <div className="text-lg font-mono font-bold text-red-800 bg-red-100 rounded px-3 py-1 inline-block">
                      {formatCountdown(lockCountdown)}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label htmlFor="login-email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-4 py-2 text-base focus:outline-none focus:ring-2 focus:ring-cyan-600"
                  required
                  disabled={isLocked && lockCountdown > 0}
                />
              </div>
              
              <div>
                <label htmlFor="login-password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  id="login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-4 py-2 text-base focus:outline-none focus:ring-2 focus:ring-cyan-600"
                  required
                  disabled={isLocked && lockCountdown > 0}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={handleForgotPasswordClick}
                  className="text-sm text-cyan-600 hover:text-cyan-500"
                >
                  Forgot password?
                </button>
                
                <button
                  type="button"
                  onClick={handleCreateAccount}
                  className="text-sm text-cyan-600 hover:text-cyan-500"
                >
                  Don't have an account?
                </button>
              </div>
              
              {error && !isLocked && (
                <div className={`border rounded-md p-3 ${requiresVerification ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`}>
                  {requiresVerification ? (
                    <div>
                      <div className="flex items-start gap-2 mb-2">
                        <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <div>
                          <p className="text-sm font-medium text-amber-800">
                            Please verify your email first
                          </p>
                          <p className="text-sm text-amber-700 mt-1">
                            Check your inbox for a verification link. This helps protect your sensitive estate data.
                          </p>
                        </div>
                      </div>
                      
                      {resendSuccess && (
                        <div className="bg-green-100 text-green-700 text-sm px-3 py-2 rounded mb-2">
                          âœ“ Verification email sent! Check your inbox.
                        </div>
                      )}
                      
                      <button
                        type="button"
                        onClick={handleResendVerification}
                        disabled={resendCooldown > 0 || isResendingVerification}
                        className={`text-sm font-medium ${
                          resendCooldown > 0 || isResendingVerification
                            ? 'text-amber-400 cursor-not-allowed'
                            : 'text-amber-700 hover:text-amber-800 underline'
                        }`}
                      >
                        {isResendingVerification 
                          ? 'Sending...' 
                          : resendCooldown > 0 
                            ? `Resend in ${resendCooldown}s`
                            : 'Resend verification email'
                        }
                      </button>
                    </div>
                  ) : (
                    <p className="text-sm text-red-600">{error}</p>
                  )}
                </div>
              )}
              
              <button
                type="submit"
                disabled={isSubmitting || (isLocked && lockCountdown > 0)}
                className={`w-full bg-cyan-600 text-white rounded-md py-3 text-base font-medium hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 ${
                  isSubmitting || (isLocked && lockCountdown > 0) ? 'opacity-75 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting ? 'Logging in...' : isLocked && lockCountdown > 0 ? 'Account Locked' : 'Log In'}
              </button>
            </form>
          </div>
        </div>
      </ResponsiveContent>
      
      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
      />
    </ResponsiveContainer>
  );
};

export default LoginPage;
